import { useMemo } from 'react'
import { Card } from '../../SharedComponents/Card'
import { DataTable } from './DataTable'
import { StatusTag } from './StatusTag'
import {
  formatCurrency,
  formatDate,
  parseAmount,
} from '../../../lib/utils/format'
import type {
  Column,
  FileRow,
  FilesTableCardProps,
  StatusTagVariant,
} from '../types'
import styles from '../index.module.css'

const fileStatusVariant = (status: string): StatusTagVariant => {
  switch (status) {
    case 'Paid':
      return 'green'
    case 'Pending':
    case '3 Day Hold':
      return 'gold'
    case 'Bad Draft':
      return 'orange'
    case 'Cancelled':
      return 'red'
    case 'Limbo':
      return 'blue'
    case 'Clawback':
      return 'purple'
    default:
      return 'default'
  }
}

// TODO: move to BE
const compareDates = (
  a: string | null | undefined,
  b: string | null | undefined,
) =>
  new Date(a ?? '1900-01-01').getTime() - new Date(b ?? '1900-01-01').getTime()

const compareAmount = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
) => (parseAmount(a) ?? 0) - (parseAmount(b) ?? 0)

const compareString = (
  a: string | null | undefined,
  b: string | null | undefined,
) => (a ?? '').localeCompare(b ?? '')

export default function FilesTableCard({
  rows,
  statusFilter,
  loading,
}: FilesTableCardProps) {
  const filtered = useMemo(() => {
    if (statusFilter.length === 0) return rows
    return rows.filter((r) => statusFilter.includes(r.file_status))
  }, [rows, statusFilter])

  const columns: Column<FileRow>[] = [
    {
      title: 'DS #',
      key: 'dsid',
      accessor: 'dsid',
      sorter: (a, b) => a.dsid.localeCompare(b.dsid),
    },
    {
      title: 'Client',
      key: 'client',
      accessor: 'client',
      sorter: (a, b) => a.client.localeCompare(b.client),
    },
    {
      title: 'State',
      key: 'state',
      accessor: 'state',
      sorter: (a, b) => a.state.localeCompare(b.state),
    },
    {
      title: 'True FPD',
      key: 'true_fpd',
      render: (r) => formatDate(r.true_fpd),
      sorter: (a, b) => compareDates(a.true_fpd, b.true_fpd),
    },
    {
      title: 'FP Clear',
      key: 'fp_clear',
      render: (r) => formatDate(r.fp_clear),
      sorter: (a, b) => compareDates(a.fp_clear, b.fp_clear),
    },
    {
      title: 'Cancel Date',
      key: 'cancel_date',
      render: (r) => formatDate(r.cancel_date),
      sorter: (a, b) => compareDates(a.cancel_date, b.cancel_date),
    },
    {
      title: 'True Debt',
      key: 'true_debt',
      render: (r) => formatCurrency(r.true_debt, 0),
      sorter: (a, b) => compareAmount(a.true_debt, b.true_debt),
    },
    {
      title: 'Last Draft Status',
      key: 'last_draft_status',
      accessor: 'last_draft_status',
      sorter: (a, b) => compareString(a.last_draft_status, b.last_draft_status),
    },
    {
      title: 'Last Draft Date',
      key: 'last_draft_date',
      render: (r) => formatDate(r.last_draft_date),
      sorter: (a, b) => compareDates(a.last_draft_date, b.last_draft_date),
    },
    {
      title: 'File Status',
      key: 'file_status',
      render: (r) =>
        r.file_status ? (
          <StatusTag variant={fileStatusVariant(r.file_status)}>
            {r.file_status}
          </StatusTag>
        ) : null,
      sorter: (a, b) => a.file_status.localeCompare(b.file_status),
    },
    {
      title: 'Per Deal $',
      key: 'per_deal',
      render: (r) => formatCurrency(r.per_deal),
      sorter: (a, b) => compareAmount(a.per_deal, b.per_deal),
    },
  ]

  return (
    <Card>
      <div className={styles.cardHeader}>MY FILES</div>
      <DataTable
        columns={columns}
        data={filtered}
        rowKey="dsid"
        loading={loading}
        // To re-enable pagination, pass `pageSize={10}` (or any positive number).
      />
    </Card>
  )
}
